// ScopeSummary — the READ face of a person's scope (ADR-050 §2 · §3), shared by
// the person page's Access card, the peek, the wizard's review and the editor's
// footer: one line per kind — the picks as chips wearing their kind's glyph,
// "All" one chip (the line names the kind), the grants outside the viewer's reach a COUNT (kept,
// never named — ADR-050 §1 · ADR-066 §9); the whole project one word; an
// administrator one word; nothing yet = "No scope yet" (the editor is the
// surface's primary act — never a link here). Strings: the `users` namespace;
// the glyphs the registry's.

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserScopeRead } from '@ems-app/schemas/users/responses';
import { icons } from '../../components/ui/icons.js';
import { PlaceTag } from '../../components/patterns/PlaceTag.js';

export interface ScopeSummaryProps {
  scope: UserScopeRead;
  className?: string;
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-baseline sm:gap-3">
      <span className="shrink-0 text-[var(--color-text-secondary)] sm:w-28">{label}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

export function ScopeSummary({ scope, className = '' }: ScopeSummaryProps) {
  const { t } = useTranslation('users');
  const word = (text: string) => <p className={`text-sm text-[var(--color-text-primary)] ${className}`}>{text}</p>;
  if (scope.kind === 'admin') return word(t('scope.kind.admin'));
  if (scope.kind === 'whole') return word(t('scope.kind.whole'));
  if (scope.kind === 'none') return <p className={`text-sm text-[var(--color-text-secondary)] ${className}`}>{t('scope.kind.none')}</p>;
  const kept = (n: number) => (n > 0 ? <span className="text-xs text-[var(--color-text-secondary)]">{t('scope.outsideKept', { count: n })}</span> : null);
  const s = scope.sites;
  const sitesOutside = s.outside.zones + s.outside.clusters + s.outside.sites;
  const sitesEmpty = !s.all && s.zones.length + s.clusters.length + s.sites.length === 0;
  const depotsEmpty = !scope.depots.all && scope.depots.items.length === 0;
  const pools = scope.vehicle_pools;
  const poolsEmpty = pools === null || (!pools.all && pools.items.length === 0);
  const teams = scope.teams;
  const teamsEmpty = teams === null || (!teams.all && teams.items.length === 0);
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {(!sitesEmpty || sitesOutside > 0) && (
        <Line label={t('scope.sections.sites')}>
          {s.all ? (
            <PlaceTag glyph={icons.site}>{t('scope.all')}</PlaceTag>
          ) : (
            <>
              {s.zones.map((z) => (
                <PlaceTag key={`z${z.id}`} glyph={icons.zone}>{z.name}</PlaceTag>
              ))}
              {s.clusters.map((c) => (
                <PlaceTag key={`c${c.id}`} glyph={icons.cluster}>{c.name}</PlaceTag>
              ))}
              {s.sites.map((x) => (
                <PlaceTag key={x.id} glyph={icons.site}>{x.name}</PlaceTag>
              ))}
            </>
          )}
          {kept(sitesOutside)}
        </Line>
      )}
      {(!depotsEmpty || scope.depots.outside > 0) && (
        <Line label={t('scope.sections.depots')}>
          {scope.depots.all ? <PlaceTag glyph={icons.warehouse}>{t('scope.all')}</PlaceTag> : scope.depots.items.map((d) => <PlaceTag key={d.id} glyph={icons.warehouse}>{d.name}</PlaceTag>)}
          {kept(scope.depots.outside)}
        </Line>
      )}
      {pools !== null && (!poolsEmpty || pools.outside > 0) && (
        <Line label={t('scope.sections.pools')}>
          {pools.all ? <PlaceTag glyph={icons.vehicle}>{t('scope.all')}</PlaceTag> : pools.items.map((p) => <PlaceTag key={p.id} glyph={icons.vehicle}>{p.name}</PlaceTag>)}
          {kept(pools.outside)}
        </Line>
      )}
      {teams !== null && (!teamsEmpty || teams.outside > 0) && (
        <Line label={t('scope.sections.teams')}>
          {teams.all ? <PlaceTag glyph={icons.team}>{t('scope.all')}</PlaceTag> : teams.items.map((x) => <PlaceTag key={x.id} glyph={icons.team}>{x.name}</PlaceTag>)}
          {kept(teams.outside)}
        </Line>
      )}
    </div>
  );
}
